import { Link } from 'react-router-dom';
import type { FixtureView } from '../lib/api';
import { kickoffLabel } from '../lib/utils';

interface Props {
  fixture: FixtureView;
}

const STATUS_BADGE: Record<FixtureView['predictionWindowStatus'], { text: string; cls: string }> = {
  OPEN: { text: 'PREDICT', cls: 'bg-pitch text-bg-night' },
  CLOSING_SOON: { text: 'CLOSING', cls: 'bg-trophy-gold text-bg-night animate-pulse' },
  LOCKED: { text: 'LOCKED', cls: 'bg-bg-darker text-text-faint border border-border-base' },
};

export function FixtureCard({ fixture }: Props) {
  const badge = STATUS_BADGE[fixture.predictionWindowStatus] ?? STATUS_BADGE.OPEN;
  const home = fixture.homeTeamShortName ?? fixture.homeTeamName ?? `Team #${fixture.homeTeamId}`;
  const away = fixture.awayTeamShortName ?? fixture.awayTeamName ?? `Team #${fixture.awayTeamId}`;
  return (
    <Link
      to={`/fixture/${fixture.id}`}
      className="block rounded-xl border border-border-base bg-bg-card p-4 active:scale-[0.99] transition-transform"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {fixture.competitionShortName && (
            <span
              className="shrink-0 rounded-md bg-bg-elevated border border-border-base px-1.5 py-0.5 font-display-alt text-[10px] uppercase tracking-wider text-text-dim"
              title={fixture.competitionName ?? undefined}
            >
              {fixture.competitionShortName}
            </span>
          )}
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint truncate">
            {kickoffLabel(fixture.kickoffAt)}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-md px-2 py-0.5 font-display-alt text-[10px] uppercase tracking-wider ${badge.cls}`}
        >
          {badge.text}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {fixture.homeTeamCrest && (
            <img src={fixture.homeTeamCrest} alt="" className="w-6 h-6 object-contain shrink-0" />
          )}
          <span className="font-chunky text-base text-text-base truncate">{home}</span>
        </div>
        <span className="font-mono text-text-faint text-xs shrink-0">vs</span>
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <span className="font-chunky text-base text-text-base truncate text-right">{away}</span>
          {fixture.awayTeamCrest && (
            <img src={fixture.awayTeamCrest} alt="" className="w-6 h-6 object-contain shrink-0" />
          )}
        </div>
      </div>
    </Link>
  );
}
